import React, { useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { LoadingFeedback } from 'components/Common/LoadingModal';

interface IAIInterface {
  aiSums?: any;
  aiLoading?: boolean;
  forceShowAiEvaluation?: boolean;
}

const AIEvalProduct: React.FC<IAIInterface> = ({
  aiSums,
  aiLoading,
  forceShowAiEvaluation,
}) => {
  const [showAiEvaluation, setShowAiEvaluation] = useState(false);
  const isAiEvaluationVisible = forceShowAiEvaluation || showAiEvaluation;
  const latestSummary = aiSums?.[0];
  const riskLabel = latestSummary?.risk_level || 'N/A';
  const riskClass = String(riskLabel).toLowerCase().includes('low')
    ? 'low'
    : String(riskLabel).toLowerCase().includes('high')
    ? 'high'
    : 'medium';

  const keyFactorsDisplay = (arr: any, compact = false) => {
    if (!arr || arr.length === 0) {
      return <p>No key factors identified.</p>;
    }

    return (
      <div
        className={
          compact
            ? 'product-screening-risk-factor-list compact'
            : 'product-screening-risk-factor-list'
        }
      >
        {arr.map((factor: any, index: number) => (
          <div key={index}>
            <div>
              <strong>List Name:</strong> {factor['List Name']}
            </div>
            <div>
              <strong>HS Code:</strong> {factor['HS Code']}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className='entity-screening-insight-grid product-screening-ai-grid'>
        <div className='entity-screening-risk-card'>
          <div className='entity-screening-card-heading'>
            <ShieldOutlinedIcon sx={{ fontSize: 20 }} />
            <span>Overall Risk Evaluation</span>
          </div>
          <div className={`entity-screening-risk-value ${riskClass}`}>
            {aiLoading ? 'Evaluating' : riskLabel}
          </div>
          <div className='product-screening-risk-factors'>
            <h4>Key Factors</h4>
            {keyFactorsDisplay(latestSummary?.key_factors, true)}
          </div>
          {latestSummary?.screenDate && (
            <small>Generated {latestSummary.screenDate}</small>
          )}
        </div>

        <div className='entity-screening-info-card active'>
          <div className='entity-screening-card-icon'>
            <AutoAwesomeIcon sx={{ fontSize: 22 }} />
          </div>
          <div>
            <h3>AI Evaluation</h3>
            <p>{aiLoading ? 'Generating now' : 'Generated from matches'}</p>
          </div>
          <span>
            AI analysis of product screening risk, trade burden, and key factors
          </span>
          {!forceShowAiEvaluation && (
            <button
              type='button'
              onClick={() => setShowAiEvaluation((prev) => !prev)}
            >
              {showAiEvaluation ? 'Hide Results' : 'View AI Evaluation'}
            </button>
          )}
        </div>
      </div>

      {isAiEvaluationVisible && (
        <div className='entity-screening-detail-stack product-screening-ai-detail-stack'>
          <section className='entity-screening-detail-panel'>
            {!forceShowAiEvaluation && (
              <button
                type='button'
                className='entity-screening-detail-close'
                onClick={() => setShowAiEvaluation(false)}
              >
                x
              </button>
            )}
            <div className='entity-screening-detail-title'>
              <span className='entity-screening-card-icon'>
                <AutoAwesomeIcon sx={{ fontSize: 22 }} />
              </span>
              <h3>AI Evaluation</h3>
            </div>
            {aiLoading ? (
              <LoadingFeedback size='small' />
            ) : (
              aiSums.map((summary: any, i: number) => (
                <div key={i} className='entity-screening-detail-body'>
                  <div className='product-screening-ai-summary'>
                    <div className='product-screening-ai-metrics'>
                      <div>
                        <span>HTS Code</span>
                        <strong>{summary.hs_code || 'N/A'}</strong>
                      </div>
                      <div>
                        <span>Trade Type</span>
                        <strong>{summary.trade_category || 'N/A'}</strong>
                      </div>
                      <div>
                        <span>Risk</span>
                        <strong className={riskClass}>
                          {summary.risk_level || 'N/A'}
                        </strong>
                      </div>
                      <div>
                        <span>Burden</span>
                        <strong>{summary.burden || 'N/A'}</strong>
                      </div>
                    </div>
                    <p className='entity-screening-detail-summary'>
                      {summary.explanation ||
                        'No AI summary was returned for this screening.'}
                    </p>
                  </div>

                  <div className='entity-screening-top-matches entity-screening-top-matches-card product-screening-key-factors'>
                    <h4 className='screening-ai-sum-header-text'>
                      Key Factors
                    </h4>
                    <div className='screening-ai-sum-result-text'>
                      {keyFactorsDisplay(summary.key_factors)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </section>
        </div>
      )}
    </>
  );
};

export default AIEvalProduct;
